import { useApolloClient } from "@apollo/client";
import { AttachmentInput } from "@msys/attachment";
import { getDataOrNull } from "@msys/common";
import { CardItem, CollapseSection, Modal, ModalOpenButton } from "@msys/ui";
import { Edit as EditIcon } from "@mui/icons-material";
import { IconButton, Stack, Tooltip } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Formik } from "formik";
import { uniqBy, uniqueId } from "lodash-es";
import { useSnackbar } from "notistack";
import React from "react";
import {
  AttachmentSnapshot,
  ItemAttachment,
  QuotePdfCalculationType,
} from "../../../../clients/graphqlTypes.js";
import { buildPdfAttachmentFilename } from "../../crm/buildPdfAttachmentFilename.js";
import {
  CrmSendEmailForm,
  FormValues,
  UrlAttachment,
  useCrmSendEmail,
} from "../../crm/CrmSendEmailForm.js";
import { crmContactToEmailRecipient } from "../../crm/utils.js";
import { useAllProjectContacts } from "../../projects/useAllProjectContacts.js";
import { useProjectContracteeInvite } from "../../projects/useProjectContracteeInvite.js";
import { InviteContracteeField } from "../form-fields/InviteContracteeField.js";
import {
  PdfDownloadArgs,
  useDownloadQuotePdf,
} from "../hooks/useDownloadQuotePdf.js";
import { SelectQuotePdf } from "../inputs/SelectQuotePdf.js";
import { QuotePreviewListItem } from "../QuotePreviewListItem.js";
import { QuotePublishingInfoModal } from "./QuotePublishingInfoModal.js";
import {
  SendQuoteViaEmailModal_ProjectFragment,
  SendQuoteViaEmailModal_QuoteFragment,
  useSendQuoteViaEmailQuery,
} from "./SendQuoteViaEmailModal.generated.js";

interface Props {
  quoteId: string;
  quote: SendQuoteViaEmailModal_QuoteFragment;
  projectId: string;
  project: SendQuoteViaEmailModal_ProjectFragment;
  calculationType: QuotePdfCalculationType;
  pdfTemplateArgs?: PdfDownloadArgs | null;
  handleClose: () => void;
  handleComplete?: (handleClose: () => void) => Promise<void>;
}

export const SendQuoteViaEmailModal = ({
  quoteId,
  quote,
  projectId,
  project,
  handleClose,
  handleComplete,
  calculationType,
  pdfTemplateArgs: passedPdfTemplateArgs,
}: Props) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate(["QuoteSend", "Global"]);

  const {
    recipientOptions,
    urlAttachments,
    getAttachmentsOnSubmit,
    query,
    pdfTemplateArgs,
    setPdfTemplateArgs,
  } = useSendQuoteViaEmail({
    projectId,
    quoteId,
    calculationType,
    pdfTemplateArgs: passedPdfTemplateArgs,
  });

  const context = React.useMemo(
    () =>
      ({
        type: "QUOTE",
        id: quoteId,
      }) as const,
    [quoteId]
  );

  const {
    initialValues,
    validationSchema,
    handleSendEmail,
    emailTemplates,
    templatesLoading,
    templatesError,
  } = useCrmSendEmail({
    urlAttachments,
    getAttachmentsOnSubmit,
    emailTemplateContext: "QUOTE",
    context,
  });

  const { canInviteContractee, handleInviteContractee } =
    useProjectContracteeInvite(project);

  const handleSubmit = async (
    values: FormValues & { inviteContractee: boolean }
  ) => {
    await handleSendEmail(values);
    enqueueSnackbar(
      t("Quote was sent to client", {
        ns: "QuoteSend",
      })
    );

    if (values.inviteContractee) {
      if (!values.recipient?.crmPersonId) throw new Error("CrmPerson missing");

      await handleInviteContractee(values.recipient.crmPersonId);
    }

    if (handleComplete) {
      await handleComplete(handleClose);
    } else {
      handleClose();
    }
  };

  const formId = React.useMemo(() => uniqueId(), []);

  return (
    <Formik<FormValues & { inviteContractee: boolean }>
      enableReinitialize
      initialValues={{ ...initialValues, inviteContractee: false }}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      validateOnChange={false}
      validateOnBlur={false}
    >
      {formikProps => (
        <Modal
          title={t("Send quote to client", {
            ns: "QuoteSend",
          })}
          handleClose={handleClose}
          actionButtons={[
            {
              label: t("Cancel", { ns: "Global" }),
              handleClick: handleClose,
              buttonProps: {
                variant: "text",
                disabled: formikProps.isSubmitting,
              },
            },
            {
              label: t("Send", { ns: "Global" }),
              buttonProps: {
                loading: formikProps.isSubmitting,
                type: "submit",
                form: formId,
              },
            },
          ]}
          isLoading={query.loading || templatesLoading}
          maxWidth="md"
        >
          <Stack spacing={2}>
            <CardItem>
              <QuotePreviewListItem
                quote={quote}
                Action={
                  <ModalOpenButton
                    Modal={QuotePublishingInfoModal}
                    modalProps={{ projectId, quoteId }}
                  >
                    <IconButton size="small" color="primary">
                      <Tooltip title={t("Edit", { ns: "Global" })}>
                        <EditIcon fontSize="small" />
                      </Tooltip>
                    </IconButton>
                  </ModalOpenButton>
                }
              />
            </CardItem>

            <CrmSendEmailForm
              recipientOptions={recipientOptions}
              additionalFields={
                <CollapseSection
                  title={t("PDF attachment", { ns: "QuoteSend" })}
                  isInitiallyExpanded={true}
                >
                  <SelectQuotePdf
                    doc={quote}
                    label={t("Choose PDF template", { ns: "QuoteSend" })}
                    value={pdfTemplateArgs}
                    onChange={setPdfTemplateArgs}
                    calculationType={calculationType}
                  />
                </CollapseSection>
              }
              formId={formId}
              availableTemplates={emailTemplates}
              context={context}
              emailTemplateContext="QUOTE"
              templatesLoading={templatesLoading}
              templatesError={templatesError}
            />

            {canInviteContractee && (
              <InviteContracteeField
                project={project}
                checked={formikProps.values.inviteContractee}
                handleChange={checked => {
                  formikProps.setFieldValue("inviteContractee", checked);
                }}
                disabled={!formikProps.values.recipient?.crmPersonId}
              />
            )}
          </Stack>
        </Modal>
      )}
    </Formik>
  );
};

export function useSendQuoteViaEmail({
  projectId,
  quoteId,
  calculationType,
  pdfTemplateArgs: passedPdfTemplateArgs,
}: {
  projectId: string;
  quoteId: string;
  calculationType: QuotePdfCalculationType;
  pdfTemplateArgs?: PdfDownloadArgs | null;
}) {
  const { t } = useTranslate(["QuoteSend", "Quote"]);

  const [pdfTemplateArgs, setPdfTemplateArgs] = React.useState<PdfDownloadArgs>(
    passedPdfTemplateArgs ?? {
      templateType: "none",
      calculationType,
    }
  );

  const { handleDownload } = useDownloadQuotePdf({ projectId, docId: quoteId });

  const { contacts: projectContacts } = useAllProjectContacts(projectId);

  const client = useApolloClient();
  const query = useSendQuoteViaEmailQuery({
    client,
    variables: {
      projectId,
      quoteId,
    },
    fetchPolicy: "no-cache",
  });

  const quote = getDataOrNull((query.data ?? query.previousData)?.quote)?.quote;
  const contractee = quote?.contractee;
  const quoteContacts = contractee
    ? [
        ...(contractee.viewerCrmCompany ? [contractee.viewerCrmCompany] : []),
        ...(contractee.viewerCrmCompany?.members ?? []),
      ]
    : [];

  const recipientOptions = uniqBy(
    [...quoteContacts, ...projectContacts].filter(
      contact => contact.id && contact.email
    ),
    contact => contact.id
  ).map(crmContactToEmailRecipient);

  const quoteAttachments = React.useMemo(
    () =>
      quote?.items
        .filter(item => item.isVisibleToOtherSide)
        .flatMap(
          item =>
            item.attachments.filter(
              attachment =>
                attachment.clientVisibility &&
                (item.isRootItem || attachment.mimeType === "application/pdf")
            ) ?? []
        ) ?? [],
    [quote?.items]
  );

  if (!query.loading && !contractee)
    throw new Error("Crm organisation is missing");
  if (!query.loading && !quote) throw new Error("Quote is missing");

  const urlAttachments = React.useMemo(() => {
    const urlAttachments: UrlAttachment[] = [];

    urlAttachments.push(
      ...quoteAttachments.map(quoteAttachment => ({
        attachment: convertAttachmentSnapshotToUrlAttachment(quoteAttachment),
        group: t("Files from quote", {
          ns: "QuoteSend",
        }),
      }))
    );

    return urlAttachments;
  }, [t, quoteAttachments]);

  const getAttachmentsOnSubmit = async (): Promise<Array<AttachmentInput>> => {
    const url = await handleDownload(pdfTemplateArgs);
    if (!url) throw new Error("Cannot create quote PDF");
    const title = buildPdfAttachmentFilename(
      t("Quote", { ns: "Quote" }),
      quote?.number
    );
    return [{ url, mimeType: "application/pdf", title }];
  };

  return {
    recipientOptions,
    urlAttachments,
    getAttachmentsOnSubmit,
    query,
    quote,
    handleDownload,
    pdfTemplateArgs,
    setPdfTemplateArgs,
  };
}

function convertAttachmentSnapshotToUrlAttachment(
  attachment: AttachmentSnapshot | ItemAttachment
): UrlAttachment["attachment"] {
  return {
    filename: attachment.title,
    mimeType: attachment.mimeType,
    url: attachment.url,
  };
}
